import React, { useState } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { InputText } from '../../../common/components/Input/InputText';
import { isValidEmail, isValidEmails } from '../../../common/utils/emailUtils';
import {
  AccommodationAccountRoleDto,
  CareTakerItem,
  translateRoleEnum,
} from '../bookingsSlice';

interface BookingMaintainersComponentProps {
  bookingId: string;
  bookingMaintainersAsRoles: AccommodationAccountRoleDto[];
  bookingMaintainers: CareTakerItem[];
  sendMaintainerBookingConfirmation: (
    maintainerAccountId: string,
    emailAddress: string,
    bookingId: string,
  ) => void; // Add handler as a prop
}

export const BookingMaintainersEmailComponent: React.FC<
  BookingMaintainersComponentProps
> = ({
  bookingId,
  bookingMaintainersAsRoles,
  bookingMaintainers,
  sendMaintainerBookingConfirmation,
}) => {
  /*
  // Create a state to track email addresses, initially using the caretakers’s email.
  const [updatedEmails, setUpdatedEmails] = useState(
    Object.fromEntries(
      bookingMaintainersAsRoles?.map(bookingMaintainerAsRole => [
        bookingMaintainerAsRole?.accountId ?? '',
        bookingMaintainerAsRole?.emails !== null &&
        bookingMaintainerAsRole?.emails !== undefined
          ? (bookingMaintainerAsRole?.emails[0]?.emailAddress ?? '')
          : '',
      ]),
    ),
  );

   */
  const idsFromRoles = new Set(bookingMaintainersAsRoles.map(role => role.id));
  /*
  // 1. Create initial emails from roles
  const roleEmails = Object.fromEntries(
    bookingMaintainersAsRoles.map(role => [
      role.accountId ?? '',
      role.emails?.[0]?.emailAddress ?? '',
    ]),
  );
*/
  // 1. Create initial emails from roles
  // UPDATE 12.6.2025. HAve to take account that
  // IF caretaker has no bookingNoteEmail or no email has isBookingNoteEmail true

  const roleEmails = Object.fromEntries(
    bookingMaintainersAsRoles
      .filter(role => role.bookingNoteSettings?.bookingNoteByEmail)
      .map(role => {
        const emails = role.emails
          ?.filter(e => e.isBookingNoteEmail)
          .map(e => e.emailAddress?.trim())
          .filter(email => !!email);

        return [role.accountId ?? '', emails?.join('; ') ?? ''];
      })
      .filter(([accountId, email]) => accountId && email), // remove blanks
  );

  // 2. Add caretaker emails, only if not already in roleEmails
  const caretakerEmails = Object.fromEntries(
    bookingMaintainers
      .filter(ct => !roleEmails[ct.id ?? '']) // skip if already present
      .map(ct => [ct.id ?? '', ct.email ?? '']),
  );

  // 3. Merge both maps
  const mergedEmails = { ...caretakerEmails, ...roleEmails };

  // 4. Set state
  const [updatedEmails, setUpdatedEmails] = useState(mergedEmails);

  // 5. Remove caretakers whose `id` exists in `bookingMaintainersAsRoles`
  const filteredBookingMaintainers = bookingMaintainers.filter(
    ct => !idsFromRoles.has(ct.id),
  );
  // Handler to update the email in state when the user edits the text field
  const handleEmailChange = (maintainerAccountId, newEmail) => {
    setUpdatedEmails(prevEmails => ({
      ...prevEmails,
      [maintainerAccountId]: newEmail,
    }));
  };

  // Call the parent handler when sending the confirmation
  const handleSendConfirmation = (maintainerAccountId: string) => {
    const emailToSend = updatedEmails[maintainerAccountId]; // This will either be the updated or original email
    sendMaintainerBookingConfirmation(
      maintainerAccountId,
      emailToSend,
      bookingId,
    ); // Call the parent handler
  };

  return (
    <div>
      {(bookingMaintainersAsRoles.length > 0 ||
        filteredBookingMaintainers.length > 0) && (
        <h3 className="h4 mb-16">Lähetä varausvahvistus huoltajille</h3>
      )}

      {/* Display maintainers from roles */}
      {bookingMaintainersAsRoles.map(bookingMaintainerAsRole => (
        <div className="mb-24" key={bookingMaintainerAsRole.id}>
          <Form.Label htmlFor="clientEmail" className="required">
            {translateRoleEnum(bookingMaintainerAsRole.roleEnum)} -{' '}
            {bookingMaintainerAsRole.roleFirstName}{' '}
            {bookingMaintainerAsRole.roleLastName}
          </Form.Label>
          <InputGroup className="flex-nowrap">
            <InputText
              Id="maintainerEmail"
              Name="maintainerEmail"
              IsRequired
              HandleChange={e =>
                handleEmailChange(
                  bookingMaintainerAsRole.accountId,
                  e.target.value,
                )
              }
              Value={updatedEmails[bookingMaintainerAsRole.accountId] ?? ''}
              ClassName="mb-0 flex-fill"
            />
            <Button
              onClick={() =>
                handleSendConfirmation(bookingMaintainerAsRole.accountId)
              }
              disabled={
                !isValidEmails(updatedEmails[bookingMaintainerAsRole.accountId])
              }
              className="ms-n8 px-16"
            >
              Lähetä
            </Button>
          </InputGroup>
        </div>
      ))}

      {/* Display caretakers not present in role list */}
      {filteredBookingMaintainers.map(bookingMaintainer => (
        <div className="mb-24" key={bookingMaintainer.id}>
          <Form.Label htmlFor="clientEmail" className="required">
            Huoltaja - {bookingMaintainer.ownerNameNumber}
          </Form.Label>
          <InputGroup className="flex-nowrap">
            <InputText
              Id="maintainerEmail"
              Name="maintainerEmail"
              IsRequired
              HandleChange={e =>
                handleEmailChange(bookingMaintainer.id, e.target.value)
              }
              Value={updatedEmails[bookingMaintainer.id] ?? ''}
              ClassName="mb-0 flex-fill"
            />
            <Button
              onClick={() => handleSendConfirmation(bookingMaintainer.id)}
              disabled={
                !updatedEmails[bookingMaintainer.id] || // disable if empty or undefined
                !updatedEmails[bookingMaintainer.id]
                  .split(';') // split string into array by ';'
                  .map(email => email.trim()) // trim spaces
                  .every(email => isValidEmail(email)) // check all emails are valid
              }
              className="ms-n8 px-16"
            >
              Lähetä
            </Button>
          </InputGroup>
        </div>
      ))}
    </div>
  );
};
